import { Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { AuthService } from '@mds/angular-core';
import { MessageService } from '@mds/angular-layout-primeng';
import { DynamicScopesService } from '@mds/authentication';
import { Observable, ReplaySubject, catchError, filter, map, of, switchMap } from 'rxjs';
import { IOrganizationSwitchRequest } from '../../organization-switch/models/organization-switch-request.model';
import { ClaimTypes } from '../models/claim-types.model';
import { MeClient, UserScopeParameterValueModel } from './api-client';

@Injectable({
  providedIn: 'root',
})
export class OrganizationSwitchService {
  private readonly organizationScope = 'planzer.id.orgid';
  private readonly switchClearedErrorState = 'switch-cleared-error';
  private readonly organizationSubject = new ReplaySubject<UserScopeParameterValueModel | undefined>();
  private readonly currentOrganizationIdParameterName = 'currentOrganizationId';
  private readonly callbackUrlParameterName = 'callbackUrl';
  private readonly organizationIdTemplateString = '{{orgId}}';

  constructor(
    @Inject(DynamicScopesService) private readonly dynamicScopesService: DynamicScopesService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly meClient: MeClient,
    private readonly messageService: MessageService
  ) {
    this.getOrganization().subscribe((organization) => {
      if (this.dynamicScopesService.has(this.organizationScope) && organization == undefined) {
        // the users organization could not be loaded despite it getting selected
        this.setSwitchClearedError();
        this.clearOrganizationSwitch();
      }
    });

    this.authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.meClient.getCurrentScopeParameterValues(ClaimTypes.Organization, true)),
        map((values) => (values.length === 1 ? values[0] : undefined)),
        catchError(() => {
          return of(undefined);
        })
      )
      .subscribe(this.organizationSubject);

    if (this.hasSwitchClearedError()) {
      messageService.add({
        severity: 'warn',
        textResource: _('Iam.OrganizationSwitch.Notification.UnauthorizedAndDeselected.Warn'),
      });
      this.unsetSwitchClearedError();
    }
  }

  getOrganization(): Observable<UserScopeParameterValueModel | undefined> {
    return this.organizationSubject.asObservable();
  }

  getOrganizations(): Observable<UserScopeParameterValueModel[]> {
    return this.meClient.getScopeParameterValues(ClaimTypes.Organization);
  }

  openOrganizationSwitch(): void {
    this.getOrganization().subscribe((organization) => {
      this.router.navigate(['organization-switch'], {
        queryParams: {
          callbackUrl: window.location.origin + `/${this.organizationIdTemplateString}/my-organization/detail`,
          currentOrganizationId: organization?.value,
        },
      });
    });
  }

  switchOrganization(organizationId: string, targetUrl?: string): void {
    this.canSwitchToOrganization(organizationId).subscribe((canSwitch) => {
      if (canSwitch) {
        this.dynamicScopesService.set(this.organizationScope, organizationId, true);
        this.authService.login(targetUrl);
      } else {
        this.openHome();
        this.messageService.add({
          severity: 'warn',
          textResource: _('Iam.OrganizationSwitch.Notification.UnauthorizedAndCancelled.Warn'),
        });
      }
    });
  }

  clearOrganizationSwitch(): void {
    this.dynamicScopesService.remove(this.organizationScope);
    this.authService.login();
  }

  formatTemplateUrl(url: string, organizationId: string): string {
    return url.replace(this.organizationIdTemplateString, organizationId);
  }

  createOrganizationSwitchRequest(route: ActivatedRouteSnapshot): IOrganizationSwitchRequest | null {
    const currentOrganizationId = route.queryParams[this.currentOrganizationIdParameterName];
    const callbackUrl = route.queryParams[this.callbackUrlParameterName];

    if (!callbackUrl) {
      return null;
    }

    return {
      callbackUrl,
      currentOrganizationId,
    };
  }

  isValidCallbackUrl(request: IOrganizationSwitchRequest): Observable<boolean> {
    const model = {
      callbackUrl: request.callbackUrl,
    };

    return this.meClient.validateCallbackUrl(model, true).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  private canSwitchToOrganization(organizationId: string): Observable<boolean> {
    return this.getOrganizations().pipe(map((orgs) => orgs.find((x) => x.value === organizationId) !== undefined));
  }

  private openHome(): void {
    this.router.navigate(['/home']);
  }

  private setSwitchClearedError(): void {
    localStorage.setItem(this.switchClearedErrorState, '1');
  }

  private unsetSwitchClearedError(): void {
    localStorage.removeItem(this.switchClearedErrorState);
  }

  private hasSwitchClearedError(): boolean {
    return localStorage.getItem(this.switchClearedErrorState) != null;
  }
}
